import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Link,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    Pagination,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    KeyboardArrowDown,
    KeyboardArrowUp,
    OpenInNew as OpenInNewIcon,
    Download as DownloadIcon,
    FilterList as FilterListIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import ClientDocumentDialog, {
    DocumentCancelChip,
    DocumentClientPicker,
    DocumentExpiryChip,
    documentChangeFileUrl,
    documentFileUrl,
    documentResponsibleName,
    documentScopeLabel,
    documentSignerName,
    documentCancelState,
    documentExpiryState,
    expiryActionLabel,
    isDocumentCancelled,
    mergeDocumentTypes,
    splitDocumentChanges,
} from '../components/ClientDocumentDialog';
import { getClientDocuments, getClientDocumentTypes } from '../redux/ducks/client';
import { useHasPermission } from '../utils/permissionUtils';
import { formatDate, formatDateTime, formatDayShort } from '../utils/dateUtils';

const EXPIRING_WITHIN_OPTIONS = [30, 60, 90];

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const PaginationBar = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1, 2),
    padding: theme.spacing(1.5, 2),
}));

const PageSizeField = styled(TextField)({
    width: 140,
});

const ContainerStyled = styled(Box)(({ theme }) => ({
    width: '100%',
    padding: theme.spacing(2),
}));

const Filters = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        gridTemplateColumns: '1fr 1fr',
    },
    [theme.breakpoints.between('md', 'lg')]: {
        gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
        '& > :first-of-type': { gridColumn: 'span 6' },
    },
    [theme.breakpoints.up('lg')]: {
        gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 2fr) minmax(150px, 1fr) minmax(130px, 1fr) minmax(120px, 1fr) minmax(150px, 1fr)',
    },
}));

const SearchRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const FiltersToggle = styled(Button)(({ theme }) => ({
    flexShrink: 0,
    [theme.breakpoints.up('sm')]: {
        display: 'none',
    },
}));

const MoreFilters = styled(Box, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    display: open ? 'grid' : 'none',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        '& > :first-of-type': {
            gridColumn: '1 / -1',
        },
    },
    [theme.breakpoints.up('sm')]: {
        display: 'contents',
    },
    [theme.breakpoints.between('md', 'lg')]: {
        '& > *': { gridColumn: 'span 3' },
        '& > :first-of-type': { gridColumn: 'span 6' },
    },
}));

const WordCell = styled(TableCell)({
    overflowWrap: 'break-word',
});

const TextCell = styled(TableCell)({
    overflowWrap: 'anywhere',
});

const DocumentCell = styled(WordCell)({
    '& > .MuiTypography-root:not(.MuiLink-root)': { overflowWrap: 'anywhere' },
});

const ActionsCell = styled(TableCell)({
    width: '1%',
    whiteSpace: 'nowrap',
});

const ClientCell = styled(WordCell, { shouldForwardProp: (prop) => prop !== 'compact' })(({ compact }) => ({
    ...(compact && { width: '42%' }),
}));

const NameLink = styled(Link)({
    textAlign: 'left',
    verticalAlign: 'baseline',
    overflowWrap: 'inherit',
});

const DetailsList = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'minmax(100px, max-content) minmax(0, 1fr)',
    gap: theme.spacing(1.25, 2),
    alignItems: 'baseline',
    overflowWrap: 'anywhere',
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: theme.spacing(0.25),
        '& dd': { marginBottom: theme.spacing(1) },
    },
    '& dt': { color: theme.palette.text.secondary },
    '& dd': { margin: 0 },
}));

const DetailsTerms = styled(Typography)(({ theme }) => ({
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
}));

const DetailsActions = styled(DialogActions)(({ theme }) => ({
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    '& > :not(:first-of-type)': { marginLeft: 0 },
}));

const DetailsSpacer = styled(Box)({
    flex: 1,
});

const DocumentsTable = styled(Table, { shouldForwardProp: (prop) => prop !== 'compact' })(({ theme, compact }) => ({
    ...(compact && {
        tableLayout: 'fixed',
        '& .MuiTableCell-root:not(:first-of-type)': { paddingLeft: theme.spacing(1), paddingRight: theme.spacing(1) },
    }),
}));

const ChipRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
}));

const StackedChips = styled(ChipRow)(({ theme }) => ({
    marginTop: theme.spacing(0.5),
}));

const DocumentTableRow = styled(TableRow, { shouldForwardProp: (prop) => prop !== 'cancelled' })(({ theme, cancelled }) => ({
    ...(cancelled && { '& > .MuiTableCell-root': { color: theme.palette.text.secondary } }),
}));

const ExpandCell = styled(TableCell)(({ theme }) => ({
    width: theme.spacing(6),
    paddingRight: 0,
    [theme.breakpoints.down('sm')]: {
        width: theme.spacing(4.5),
        paddingLeft: theme.spacing(0.5),
    },
}));

const DetailCell = styled(TableCell)({
    paddingTop: 0,
    paddingBottom: 0,
});

const TermsBlock = styled(Typography)(({ theme }) => ({
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    padding: theme.spacing(1, 0, 2),
}));

const FileActions = styled(Box)({
    display: 'flex',
    flexWrap: 'nowrap',
});

const StackedActions = styled(FileActions)(({ theme }) => ({
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(-0.625),
}));

const CenterBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(6),
}));

const SignedChip = ({ item }) => (
    <Tooltip title={item.signedDate ? `Signed on ${formatDate(item.signedDate)}` : ''}>
        <Chip size="small" label={item.signed ? 'Signed' : 'Not Signed'} color={item.signed ? 'success' : 'error'} />
    </Tooltip>
);

const ExpiresCell = styled(TableCell)({
    whiteSpace: 'nowrap',
});

const ChangeLine = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1, 1.5),
    marginTop: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
}));

const ChangeLineHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    minHeight: theme.spacing(3.5),
}));

const ChangeSummary = styled(Typography)({
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
});

const changeStatusLabel = (change, split) => {
    const day = formatDayShort(change.effectiveDate);
    if (change === split.inEffect) return `In effect since ${day}`;
    if (split.upcoming.includes(change)) return `Upcoming, effective ${day}`;
    return `Effective ${day}`;
};

const DocumentChips = ({ item }) => (
    <>
        {(item.signed || !isDocumentCancelled(item)) && <SignedChip item={item} />}
        <DocumentCancelChip document={item} />
    </>
);

const DocumentActionButtons = ({ item, hasFile, onEdit }) => (
    <>
        {hasFile && (
            <>
                <Tooltip title="View">
                    <IconButton size="small" href={documentFileUrl(item.client._id, item._id)} target="_blank" rel="noopener">
                        <OpenInNewIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                    <IconButton size="small" href={documentFileUrl(item.client._id, item._id, true)}>
                        <DownloadIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </>
        )}
        {onEdit && (
            <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit(item)}>
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        )}
    </>
);

const effectiveTerms = (item) => {
    const { inEffect } = splitDocumentChanges(item);
    return inEffect ? { text: inEffect.summary, since: inEffect.effectiveDate } : { text: item.terms, since: null };
};

const DocumentDetailsDialog = ({ item, onClose, onEdit }) => {
    const hasFile = !!(item?.attachment && item?.client?._id);
    const signerName = item ? documentSignerName(item) : '';
    const responsibleName = item ? documentResponsibleName(item) : '';
    const atExpiry = item ? expiryActionLabel(item.expiryAction) : '';
    const split = item ? splitDocumentChanges(item) : null;
    return (
        <Dialog open={!!item} onClose={onClose} fullWidth maxWidth="sm">
            {item && (
                <>
                    <DialogTitle>{item.title || item.document}</DialogTitle>
                    <DialogContent dividers>
                        <DetailsList component="dl">
                            <dt>Client</dt>
                            <dd>
                                {item.client?.slug ? (
                                    <Link component={RouterLink} to={`/client/${item.client.slug}`} underline="hover">
                                        {item.client.name}
                                    </Link>
                                ) : (
                                    item.client?.name || '—'
                                )}
                            </dd>
                            <dt>Type</dt>
                            <dd>{item.document}</dd>
                            {item.title && (
                                <>
                                    <dt>Title</dt>
                                    <dd>{item.title}</dd>
                                </>
                            )}
                            <dt>Applies to</dt>
                            <dd>
                                <ScopeLink item={item} />
                            </dd>
                            <dt>Status</dt>
                            <dd>
                                <ChipRow>
                                    <DocumentChips item={item} />
                                </ChipRow>
                            </dd>
                            {item.signedDate && (
                                <>
                                    <dt>Signed on</dt>
                                    <dd>{formatDate(item.signedDate)}</dd>
                                </>
                            )}
                            {signerName && (
                                <>
                                    <dt>Signer</dt>
                                    <dd>{signerName}</dd>
                                </>
                            )}
                            {item.cancelledDate && (
                                <>
                                    <dt>{documentCancelState(item) === 'pending' ? 'Cancels on' : 'Cancelled on'}</dt>
                                    <dd>{formatDate(item.cancelledDate)}</dd>
                                </>
                            )}
                            {documentExpiryState(item) && (
                                <>
                                    <dt>Expires</dt>
                                    <dd>
                                        <DocumentExpiryChip document={item} />
                                    </dd>
                                </>
                            )}
                            {atExpiry && (
                                <>
                                    <dt>At expiry</dt>
                                    <dd>{atExpiry}</dd>
                                </>
                            )}
                            {responsibleName && (
                                <>
                                    <dt>Responsible</dt>
                                    <dd>{responsibleName}</dd>
                                </>
                            )}
                            <dt>File</dt>
                            <dd>{item.attachment?.name || 'No file'}</dd>
                        </DetailsList>
                        <Typography variant="subtitle2" component="h3" mt={2}>
                            Agreement terms
                        </Typography>
                        {item.terms ? (
                            <DetailsTerms variant="body2">{item.terms}</DetailsTerms>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No terms recorded
                            </Typography>
                        )}
                        {item.termsUpdatedAt && (
                            <Typography variant="caption" color="text.secondary" component="div" mt={0.5}>
                                Updated {formatDateTime(item.termsUpdatedAt)}
                                {item.termsUpdatedBy?.fullName ? ` by ${item.termsUpdatedBy.fullName}` : ''}
                            </Typography>
                        )}
                        {split.changes.length > 0 && (
                            <>
                                <Typography variant="subtitle2" component="h3" mt={2}>
                                    Changes
                                </Typography>
                                {split.changes.map((change) => (
                                    <ChangeLine key={change._id}>
                                        <ChangeLineHeader>
                                            <Typography variant="caption" color="text.secondary" component="div">
                                                {changeStatusLabel(change, split)}
                                                {change.privacy && change.privacy !== 'Public' ? ` · ${change.privacy}` : ''}
                                            </Typography>
                                            {change.attachment && item.client?._id && (
                                                <FileActions>
                                                    <Tooltip title="View file">
                                                        <IconButton size="small" href={documentChangeFileUrl(item.client._id, item._id, change._id)} target="_blank" rel="noopener">
                                                            <OpenInNewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Download file">
                                                        <IconButton size="small" href={documentChangeFileUrl(item.client._id, item._id, change._id, true)}>
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </FileActions>
                                            )}
                                        </ChangeLineHeader>
                                        <ChangeSummary variant="body2">{change.summary}</ChangeSummary>
                                    </ChangeLine>
                                ))}
                            </>
                        )}
                    </DialogContent>
                    <DetailsActions>
                        {hasFile && (
                            <>
                                <Button startIcon={<OpenInNewIcon />} href={documentFileUrl(item.client._id, item._id)} target="_blank" rel="noopener">
                                    View file
                                </Button>
                                <Button startIcon={<DownloadIcon />} href={documentFileUrl(item.client._id, item._id, true)}>
                                    Download
                                </Button>
                            </>
                        )}
                        <DetailsSpacer />
                        {onEdit && (
                            <Button startIcon={<EditIcon />} onClick={() => onEdit(item)}>
                                Edit
                            </Button>
                        )}
                        <Button variant="contained" onClick={onClose}>
                            Close
                        </Button>
                    </DetailsActions>
                </>
            )}
        </Dialog>
    );
};

const scopePath = (item) => {
    const { scope, client } = item;
    if (!scope?.type) return client?.slug ? `/client/${client.slug}` : null;
    if (!scope.accountSlug) return null;
    return scope.type === 'Project' ? `/account/${scope.accountSlug}/project/${scope.id}` : `/account/${scope.accountSlug}`;
};

const ScopeLink = ({ item }) => {
    const label = documentScopeLabel(item.scope) || 'Whole client';
    const to = scopePath(item);
    return to ? (
        <Link component={RouterLink} to={to} underline="hover">
            {label}
        </Link>
    ) : (
        label
    );
};

const LAYOUT_COLUMNS = { compact: 2, medium: 5, wide: 8 };

const DocumentRow = React.memo(({ item, showExpand, layout, onEdit, onView }) => {
    const [open, setOpen] = useState(false);
    const clientId = item.client?._id;
    const signerName = documentSignerName(item);
    const hasFile = !!(item.attachment && clientId);
    const hasActions = hasFile || !!onEdit;
    const compact = layout === 'compact';
    const actions = hasActions ? <DocumentActionButtons item={item} hasFile={hasFile} onEdit={onEdit} /> : null;
    const terms = effectiveTerms(item);
    return (
        <Fragment>
            <DocumentTableRow hover cancelled={isDocumentCancelled(item)}>
                {showExpand && (
                    <ExpandCell>
                        {terms.text && (
                            <Tooltip title={open ? 'Hide terms' : 'Show terms'}>
                                <IconButton size="small" onClick={() => setOpen((v) => !v)}>
                                    {open ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                        )}
                    </ExpandCell>
                )}
                <ClientCell compact={compact}>
                    <NameLink component="button" underline="hover" onClick={() => onView(item)}>
                        {item.client?.name || '—'}
                    </NameLink>
                </ClientCell>
                <DocumentCell>
                    <NameLink component="button" underline="hover" color="inherit" onClick={() => onView(item)}>
                        {item.document}
                    </NameLink>
                    {layout !== 'wide' && item.title && (
                        <Typography variant="body2" color="text.secondary">
                            {item.title}
                        </Typography>
                    )}
                    {layout === 'medium' && (
                        <Typography variant="caption" color="text.secondary" component="div">
                            <ScopeLink item={item} />
                            {signerName && ` · Signer: ${signerName}`}
                        </Typography>
                    )}
                    {compact && (
                        <StackedChips>
                            <DocumentChips item={item} />
                            <DocumentExpiryChip document={item} />
                        </StackedChips>
                    )}
                    {compact && actions && <StackedActions>{actions}</StackedActions>}
                </DocumentCell>
                {layout === 'wide' && <TextCell>{item.title || '—'}</TextCell>}
                {layout === 'wide' && (
                    <TextCell>
                        <ScopeLink item={item} />
                    </TextCell>
                )}
                {!compact && (
                    <TableCell>
                        <ChipRow>
                            <DocumentChips item={item} />
                        </ChipRow>
                    </TableCell>
                )}
                {!compact && <ExpiresCell>{documentExpiryState(item) ? <DocumentExpiryChip document={item} /> : '—'}</ExpiresCell>}
                {layout === 'wide' && <TextCell>{signerName || '—'}</TextCell>}
                {!compact && <ActionsCell>{actions ? <FileActions>{actions}</FileActions> : '—'}</ActionsCell>}
            </DocumentTableRow>
            {terms.text && (
                <TableRow>
                    <DetailCell colSpan={LAYOUT_COLUMNS[layout] + (showExpand ? 1 : 0)}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <TermsBlock variant="body2" color="text.secondary">
                                {terms.since && (
                                    <Typography variant="caption" component="div">
                                        In effect since {formatDayShort(terms.since)}
                                    </Typography>
                                )}
                                {terms.text}
                            </TermsBlock>
                        </Collapse>
                    </DetailCell>
                </TableRow>
            )}
        </Fragment>
    );
});

const Documents = () => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canManageDocuments = hasPermission('canManageDocuments');

    const [clientFilter, setClientFilter] = useState(null);
    const [typeFilter, setTypeFilter] = useState('');
    const [typeInput, setTypeInput] = useState('');
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [fileFilter, setFileFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [expiringFilter, setExpiringFilter] = useState('');
    const [sortByExpiry, setSortByExpiry] = useState(false);
    const [fetchedTypes, setFetchedTypes] = useState([]);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDoc, setEditingDoc] = useState(null);
    const [viewingDoc, setViewingDoc] = useState(null);
    const searchTimer = useRef(null);
    const typeTimer = useRef(null);

    const fetchDocuments = useCallback(() => {
        setLoading(true);
        const params = { start: page * pageSize, limit: pageSize };
        if (clientFilter?._id) params.client = clientFilter._id;
        if (typeFilter) params.type = typeFilter;
        if (fileFilter) params.hasFile = fileFilter;
        if (statusFilter) params.status = statusFilter;
        if (expiringFilter) params.expiresWithin = expiringFilter;
        if (sortByExpiry) params.sort = 'expires';
        if (search) params.search = search;
        dispatch(
            getClientDocuments(params, (data) => {
                const nextItems = Array.isArray(data?.items) ? data.items : [];
                const nextTotal = Number(data?.total) || 0;
                const lastPage = Math.max(0, Math.ceil(nextTotal / pageSize) - 1);
                if (data && !nextItems.length && page > lastPage) {
                    setPage(lastPage);
                    return;
                }
                setLoading(false);
                if (!data) return;
                setItems(nextItems);
                setTotal(nextTotal);
            }),
        );
    }, [dispatch, page, pageSize, clientFilter, typeFilter, fileFilter, statusFilter, expiringFilter, sortByExpiry, search]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    useEffect(() => {
        if (!canManageDocuments) return undefined;
        let live = true;
        dispatch(
            getClientDocumentTypes((data) => {
                if (live && Array.isArray(data?.types)) setFetchedTypes(data.types);
            }),
        );
        return () => {
            live = false;
        };
    }, [dispatch, canManageDocuments]);

    const typeOptions = useMemo(() => mergeDocumentTypes(fetchedTypes), [fetchedTypes]);

    useEffect(
        () => () => {
            clearTimeout(searchTimer.current);
            clearTimeout(typeTimer.current);
        },
        [],
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setPage(0);
            setSearch(value.trim());
        }, 400);
    };

    const applyType = (value) => {
        const next = (value || '').trim();
        setPage(0);
        setTypeFilter(next);
    };

    const handleTypeInput = (_, value, reason) => {
        setTypeInput(value);
        clearTimeout(typeTimer.current);
        if (reason === 'reset') return;
        typeTimer.current = setTimeout(() => applyType(value), 400);
    };

    const handleTypeChange = (_, value) => {
        clearTimeout(typeTimer.current);
        applyType(typeof value === 'string' ? value : '');
    };

    const showExpand = items.some((item) => effectiveTerms(item).text);
    const isWide = useMediaQuery((theme) => theme.breakpoints.up('lg'), { noSsr: true });
    const isMedium = useMediaQuery((theme) => theme.breakpoints.up('md'), { noSsr: true });
    const layout = isWide ? 'wide' : isMedium ? 'medium' : 'compact';

    const activeFilters = [clientFilter, typeFilter, fileFilter, statusFilter, expiringFilter].filter(Boolean).length;

    const handleOpenAdd = () => {
        setEditingDoc(null);
        setDialogOpen(true);
    };

    const handleOpenEdit = useCallback((item) => {
        setViewingDoc(null);
        setEditingDoc(item);
        setDialogOpen(true);
    }, []);

    const handleSaved = () => {
        setDialogOpen(false);
        fetchDocuments();
    };

    return (
        <ContainerStyled>
            <PageHeader
                title="Documents"
                actions={
                    canManageDocuments && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
                            Add Document
                        </Button>
                    )
                }
                filters={
                    <Filters>
                        <SearchRow>
                            <TextField
                                fullWidth
                                placeholder="Search documents"
                                value={searchInput}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FiltersToggle variant="outlined" startIcon={<FilterListIcon />} onClick={() => setFiltersOpen((v) => !v)}>
                                {activeFilters ? `Filters (${activeFilters})` : 'Filters'}
                            </FiltersToggle>
                        </SearchRow>
                        <MoreFilters open={filtersOpen}>
                            <DocumentClientPicker
                                value={clientFilter}
                                placeholder="All clients"
                                onChange={(v) => {
                                    setPage(0);
                                    setClientFilter(v);
                                }}
                            />
                            <Autocomplete
                                freeSolo
                                options={typeOptions}
                                value={typeFilter}
                                inputValue={typeInput}
                                onInputChange={handleTypeInput}
                                onChange={handleTypeChange}
                                renderInput={(params) => <TextField {...params} label="Type" placeholder="All types" InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />}
                            />
                            <TextField
                                select
                                fullWidth
                                label="File"
                                value={fileFilter}
                                onChange={(e) => {
                                    setPage(0);
                                    setFileFilter(e.target.value);
                                }}
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{ displayEmpty: true }}
                            >
                                <MenuItem value="">All documents</MenuItem>
                                <MenuItem value="true">With file</MenuItem>
                                <MenuItem value="false">Without file</MenuItem>
                            </TextField>
                            <TextField
                                select
                                fullWidth
                                label="Status"
                                value={statusFilter}
                                onChange={(e) => {
                                    setPage(0);
                                    setStatusFilter(e.target.value);
                                }}
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{ displayEmpty: true }}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="expired">Expired</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                            </TextField>
                            <TextField
                                select
                                fullWidth
                                label="Expiring within"
                                value={expiringFilter}
                                onChange={(e) => {
                                    setPage(0);
                                    setExpiringFilter(e.target.value);
                                }}
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{ displayEmpty: true }}
                            >
                                <MenuItem value="">Any time</MenuItem>
                                {EXPIRING_WITHIN_OPTIONS.map((days) => (
                                    <MenuItem key={days} value={String(days)}>
                                        {days} days
                                    </MenuItem>
                                ))}
                            </TextField>
                        </MoreFilters>
                    </Filters>
                }
            />

            <TableContainer component={Paper} variant="outlined">
                <DocumentsTable size="small" compact={layout === 'compact'}>
                    <TableHead>
                        <TableRow>
                            {showExpand && <ExpandCell />}
                            <ClientCell compact={layout === 'compact'}>Client</ClientCell>
                            <TableCell>{layout === 'wide' ? 'Type' : 'Document'}</TableCell>
                            {layout === 'wide' && <TableCell>Title</TableCell>}
                            {layout === 'wide' && <TableCell>Applies to</TableCell>}
                            {layout !== 'compact' && <TableCell>Signed</TableCell>}
                            {layout !== 'compact' && (
                                <ExpiresCell sortDirection={sortByExpiry ? 'asc' : false}>
                                    <TableSortLabel
                                        active={sortByExpiry}
                                        direction="asc"
                                        onClick={() => {
                                            setPage(0);
                                            setSortByExpiry((v) => !v);
                                        }}
                                    >
                                        Expires
                                    </TableSortLabel>
                                </ExpiresCell>
                            )}
                            {layout === 'wide' && <TableCell>Signer</TableCell>}
                            {layout !== 'compact' && <ActionsCell>{canManageDocuments ? 'Actions' : 'File'}</ActionsCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!loading && items.map((item) => <DocumentRow key={`${item.client?._id}-${item._id}`} item={item} showExpand={showExpand} layout={layout} onEdit={canManageDocuments ? handleOpenEdit : undefined} onView={setViewingDoc} />)}
                    </TableBody>
                </DocumentsTable>
                {loading && (
                    <CenterBox>
                        <CircularProgress />
                    </CenterBox>
                )}
                {!loading && items.length === 0 && (
                    <CenterBox>
                        <Typography variant="body2" color="text.secondary">
                            No documents found
                        </Typography>
                    </CenterBox>
                )}
                <PaginationBar>
                    <Typography variant="body2" color="text.secondary">
                        {total ? `${page * pageSize + 1}-${Math.min(total, (page + 1) * pageSize)} of ${total}` : '0 documents'}
                    </Typography>
                    <Pagination
                        count={Math.max(1, Math.ceil(total / pageSize))}
                        page={page + 1}
                        onChange={(_, p) => setPage(p - 1)}
                        color="primary"
                        shape="rounded"
                        siblingCount={1}
                        showFirstButton
                        showLastButton
                    />
                    <PageSizeField
                        select
                        size="small"
                        label="Rows per page"
                        value={pageSize}
                        onChange={(e) => {
                            setPage(0);
                            setPageSize(Number(e.target.value));
                        }}
                    >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                            <MenuItem key={n} value={n}>
                                {n}
                            </MenuItem>
                        ))}
                    </PageSizeField>
                </PaginationBar>
            </TableContainer>

            <DocumentDetailsDialog item={viewingDoc} onClose={() => setViewingDoc(null)} onEdit={canManageDocuments ? handleOpenEdit : null} />
            {canManageDocuments && <ClientDocumentDialog open={dialogOpen} document={editingDoc} pickClient onClose={() => setDialogOpen(false)} onSaved={handleSaved} />}
        </ContainerStyled>
    );
};

export default Documents;
